import React, { useState, useEffect } from 'react';
import { getApiBaseUrlWithoutPath, getWhatsAppConfig } from '../../config/environment';
import './MyAccount.css';
import apiServices, { settingsService } from '../../services/api';
import {
  normalizeContactChannelsData,
  normalizeWhatsappDigits,
  parseContactChannelsFromStorage,
  normalizeSocialUrl,
} from '../../utils/whatsappContact';
import {
  ADMIN_NOTIFICATION_SETTINGS_KEY,
  ADMIN_NOTIFICATION_FEED_PREFS_CHANGED,
} from '../utils/adminNotificationFeedPrefs';
import { getAuthHeaders, getAdminUser, setAdminUser } from '../utils/authUtils';
import { adminUserService } from '../../services/adminUserService';
import { parseThrownAdminError } from '../utils/adminApiError';
import { validateImageFile, createImagePreview } from '../../utils/fileUtils';
import {
  uploadCreatorBannerImage,
  uploadAdminImage,
  uploadShopPromoPopupImage,
} from '../utils/uploadAdminMedia';
import {
  notifySiteBrandingUpdated,
  SITE_SETTINGS_STORAGE_KEY,
} from '../../utils/siteBranding';
import {
  writeCreatorStorefrontToStorage,
  readCreatorStorefrontFromStorage,
} from '../../utils/creatorStorefront';
import {
  buildCreatorShopPath,
  normalizeCreatorSlugInput,
  validateCreatorSlug,
  suggestCreatorSlugFromName,
  rememberCreatorSlug,
} from '../../utils/creatorSlug';
import {
  DEFAULT_SITE_PROMO_PAGE_FLAGS,
  EMPTY_PROMO_CONFIG,
  mergeCreatorPromoSources,
  normalizePromoConfig,
  readCreatorPromoFromStorage,
  writeCreatorPromoToStorage,
} from '../../utils/promoPopup';
import PromotionalPopupSettings from './PromotionalPopupSettings';
import AdminLoading from './AdminLoading.js';

const { authService } = apiServices;

/** Backend may use phone, mobile, phoneNumber, etc., or send digits as a number. */
function stringifyPhoneField(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  if (typeof raw === 'string') return raw.trim();
  return '';
}

const USER_PHONE_KEYS = [
  'phone',
  'mobile',
  'mobilePhone',
  'phoneNumber',
  'contactNumber',
  'telephone',
  'whatsapp',
];

function extractPhoneFromUserLike(obj, depth = 0) {
  if (!obj || typeof obj !== 'object' || depth > 2) return '';
  for (const key of USER_PHONE_KEYS) {
    const v = stringifyPhoneField(obj[key]);
    if (v) return v;
  }
  if (obj.contact && typeof obj.contact === 'object') {
    const nested = extractPhoneFromUserLike(obj.contact, depth + 1);
    if (nested) return nested;
  }
  return '';
}

/** Merge API + localStorage + env so admin form shows numbers already saved (same idea as storefront). */
function mergeContactChannelsForForm(apiData) {
  const api = apiData && typeof apiData === 'object' ? apiData : {};
  const stored = parseContactChannelsFromStorage() || {};
  const envDigits = normalizeWhatsappDigits(getWhatsAppConfig()?.businessNumber || '');
  const pick = (a, b) => normalizeWhatsappDigits(a) || normalizeWhatsappDigits(b) || '';
  let chat = pick(api.whatsappChatNumber, stored.whatsappChatNumber) || envDigits;
  let order = pick(api.whatsappOrderNumber, stored.whatsappOrderNumber) || envDigits;
  if (!order && chat) order = chat;
  if (!chat && order) chat = order;
  const n = normalizeContactChannelsData({ whatsappChatNumber: chat, whatsappOrderNumber: order });
  const pickUrl = (key) =>
    normalizeSocialUrl(api[key]) || normalizeSocialUrl(stored[key]) || '';
  return {
    whatsappChatNumber: n.whatsappChatNumber || '',
    whatsappOrderNumber: n.whatsappOrderNumber || '',
    twitterUrl: pickUrl('twitterUrl'),
    facebookUrl: pickUrl('facebookUrl'),
    instagramUrl: pickUrl('instagramUrl'),
    tiktokUrl: pickUrl('tiktokUrl'),
  };
}

const defaultSiteSettings = {
  siteName: 'Super Wear',
  siteDescription: 'Premium Fashion Store',
  siteEmail: 'admin@superwear.com',
  sitePhone: '+92 300 1234567',
  siteLogoUrl: '',
  currency: 'PKR',
  timezone: 'Asia/Karachi',
  maintenanceMode: false,
  allowRegistration: true,
  requireEmailVerification: false,
  promoPopupEnabled: true,
  promoPopupImageUrl: '',
  promoPopupDelaySeconds: 3,
  promoPopupAutoCloseSeconds: 8,
  ...DEFAULT_SITE_PROMO_PAGE_FLAGS,
};

const MyAccount = ({ currentUser = {} }) => {
  const phoneFromSession = () => {
    const u = getAdminUser() || currentUser || {};
    return extractPhoneFromUserLike(u);
  };
  const resolveAdminUserId = () => {
    const u = getAdminUser() || currentUser || {};
    return u._id || u.id || null;
  };
  const getTeamMemberSettingsKey = (suffix) => {
    const u = getAdminUser() || currentUser || {};
    const uid = u?._id || u?.id || u?.email || 'unknown';
    return `sheenwear_team_member_${uid}_${suffix}`;
  };
  const getTeamMemberSellerAddressKey = () => getTeamMemberSettingsKey('seller_address');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // User profile states
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    avatar: '',
    bio: '',
    department: '',
    role: '',
    joinDate: '',
    lastLogin: ''
  });

  // Password change states
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordFields, setShowPasswordFields] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Seller / Shipping Address (for invoices)
  const [sellerAddress, setSellerAddress] = useState({
    businessName: 'Super Wear',
    addressLine1: '123 Fashion Street',
    addressLine2: '',
    city: 'Karachi',
    state: 'Sindh',
    postalCode: '75000',
    country: 'Pakistan',
    phone: '+92 300 1234567',
    email: 'info@superwear.com'
  });

  const [whatsappContact, setWhatsappContact] = useState({
    whatsappChatNumber: '',
    whatsappOrderNumber: '',
    twitterUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    tiktokUrl: '',
  });

  const [siteSiteSettings, setSiteSiteSettings] = useState(defaultSiteSettings);
  const [storeLogoUploading, setStoreLogoUploading] = useState(false);
  const [savingStoreBranding, setSavingStoreBranding] = useState(false);
  const [creatorBannerUploading, setCreatorBannerUploading] = useState(false);
  const [savingCreatorStorefront, setSavingCreatorStorefront] = useState(false);
  const [creatorStorefront, setCreatorStorefront] = useState({
    bannerUrl: '',
    headline: '',
    bio: '',
    creatorSlug: '',
  });
  const [shopkeeperPromo, setShopkeeperPromo] = useState({ ...EMPTY_PROMO_CONFIG });
  const [shopPromoImagePreview, setShopPromoImagePreview] = useState('');
  const [sitePromoImagePreview, setSitePromoImagePreview] = useState('');
  const [promoPopupUploading, setPromoPopupUploading] = useState(false);
  const [promoUploadTarget, setPromoUploadTarget] = useState('site');
  const [savingSitePromo, setSavingSitePromo] = useState(false);
  const [savingShopPromo, setSavingShopPromo] = useState(false);

  const canEditStoreLogo = currentUser?.role !== 'team';
  const isTeamMember = (currentUser?.role || profileData.role) === 'team';
  const isMainAdmin = (currentUser?.role || profileData.role) === 'admin';

  // Notification/Security settings (moved from Settings page)
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    orderNotifications: true,
    stockNotifications: true,
    userNotifications: true
  });

  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    requireStrongPassword: true,
    twoFactorAuth: false,
    ipWhitelist: '',
    allowedDomains: ''
  });

  useEffect(() => {
    loadProfileData();
  }, []);

  const hydrateShopkeeperPromoFromLocal = (uid) => {
    if (!uid) return;
    const promoKey = getTeamMemberSettingsKey('promo_popup');
    let fromMemberLs = null;
    try {
      fromMemberLs = JSON.parse(localStorage.getItem(promoKey) || 'null');
    } catch {
      fromMemberLs = null;
    }
    const merged = mergeCreatorPromoSources(
      fromMemberLs || readCreatorPromoFromStorage(uid),
      null
    );
    if (merged) setShopkeeperPromo(merged);
  };

  const hydrateCreatorStorefrontFromLocal = (uid, userLike = {}) => {
    const stored = uid ? readCreatorStorefrontFromStorage(uid) : null;
    setCreatorStorefront((prev) => ({
      bannerUrl:
        stored?.bannerUrl ||
        userLike.creatorBannerUrl ||
        prev.bannerUrl ||
        '',
      headline:
        stored?.headline ||
        userLike.creatorHeadline ||
        prev.headline ||
        '',
      bio:
        stored?.bio ||
        userLike.creatorBio ||
        userLike.bio ||
        prev.bio ||
        '',
      creatorSlug:
        stored?.creatorSlug ||
        userLike.creatorSlug ||
        prev.creatorSlug ||
        '',
    }));
  };

  const loadProfileData = async () => {
    const userDataFromStorage = JSON.parse(localStorage.getItem('adminUser') || '{}');
    const uidEarly = userDataFromStorage._id || userDataFromStorage.id;
    hydrateCreatorStorefrontFromLocal(uidEarly, userDataFromStorage);
    if (userDataFromStorage.role === 'team' && uidEarly) {
      hydrateShopkeeperPromoFromLocal(uidEarly);
    }
    try {
      setLoading(true);
      setError(null);

      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) {
        setError('Admin authentication required');
        return;
      }

      // Load admin profile
      const response = await fetch(`${getApiBaseUrlWithoutPath()}/api/admin/profile`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const p =
          (data.profile && typeof data.profile === 'object' && data.profile) ||
          (data.data?.profile && typeof data.data.profile === 'object' && data.data.profile) ||
          (data.user && typeof data.user === 'object' && data.user) ||
          (data.data?.user && typeof data.data.user === 'object' && data.data.user) ||
          null;

        if (p) {
          let mergedPhone =
            extractPhoneFromUserLike(p) ||
            extractPhoneFromUserLike(userDataFromStorage) ||
            phoneFromSession() ||
            '';

          const uid = resolveAdminUserId();
          if (!mergedPhone && uid) {
            try {
              const ur = await adminUserService.getUser(uid);
              mergedPhone =
                extractPhoneFromUserLike(ur?.data) ||
                extractPhoneFromUserLike(ur?.data?.user) ||
                extractPhoneFromUserLike(ur?.user) ||
                extractPhoneFromUserLike(ur) ||
                mergedPhone;
            } catch (e) {
              console.warn('Profile phone fallback (getUser):', e?.message || e);
            }
          }

          const storedCreator = uid ? readCreatorStorefrontFromStorage(uid) : null;
          const slugFromApi = p.creatorSlug || storedCreator?.creatorSlug || '';
          const slugFromName = (() => {
            if (slugFromApi) return '';
            const v = validateCreatorSlug(suggestCreatorSlugFromName(p.name || ''));
            return v.valid ? v.slug : '';
          })();
          const mergedSlug = slugFromApi || slugFromName;
          if (uid && mergedSlug) rememberCreatorSlug(uid, mergedSlug);
          setCreatorStorefront({
            bannerUrl: p.creatorBannerUrl || storedCreator?.bannerUrl || '',
            headline: p.creatorHeadline || storedCreator?.headline || '',
            bio: p.creatorBio || p.bio || storedCreator?.bio || '',
            creatorSlug: mergedSlug,
          });

          if (uid && (p.role === 'team' || userDataFromStorage.role === 'team')) {
            const promoKey = getTeamMemberSettingsKey('promo_popup');
            let fromMemberLs = null;
            try {
              fromMemberLs = JSON.parse(localStorage.getItem(promoKey) || 'null');
            } catch {
              fromMemberLs = null;
            }
            const mergedPromo = mergeCreatorPromoSources(
              fromMemberLs || readCreatorPromoFromStorage(uid),
              { ...data, ...data.data, ...p }
            );
            if (mergedPromo) setShopkeeperPromo(mergedPromo);
          }

          setProfileData({
            name: p.name || '',
            email: p.email || '',
            phone: mergedPhone,
            avatar: p.avatar || '',
            bio: p.bio || '',
            department: p.department || '',
            role: p.role || 'admin',
            joinDate: p.joinDate || p.createdAt || '',
            lastLogin: p.lastLogin || '',
          });
        } else {
        setProfileData({
          name: userDataFromStorage.name || 'Admin User',
          email: userDataFromStorage.email || 'admin@superwear.com',
          phone: extractPhoneFromUserLike(userDataFromStorage) || phoneFromSession() || '',
          avatar: userDataFromStorage.avatar || '',
          bio: userDataFromStorage.bio || '',
          department: userDataFromStorage.department || 'Administration',
          role: userDataFromStorage.role || 'admin',
          joinDate: userDataFromStorage.joinDate || new Date().toISOString(),
          lastLogin: userDataFromStorage.lastLogin || new Date().toISOString(),
        });
          hydrateCreatorStorefrontFromLocal(uidEarly, userDataFromStorage);
        }
      } else {
        // Fallback to local storage data when API fails
        setProfileData({
          name: userDataFromStorage.name || 'Admin User',
          email: userDataFromStorage.email || 'admin@superwear.com',
          phone: extractPhoneFromUserLike(userDataFromStorage) || phoneFromSession() || '',
          avatar: userDataFromStorage.avatar || '',
          bio: userDataFromStorage.bio || '',
          department: userDataFromStorage.department || 'Administration',
          role: userDataFromStorage.role || 'admin',
          joinDate: userDataFromStorage.joinDate || new Date().toISOString(),
          lastLogin: userDataFromStorage.lastLogin || new Date().toISOString()
        });
        hydrateCreatorStorefrontFromLocal(uidEarly, userDataFromStorage);
        if (userDataFromStorage.role === 'team' && uidEarly) {
          hydrateShopkeeperPromoFromLocal(uidEarly);
        }
      }
      // Load seller address (for invoices) - per team member/account.
      try {
        const memberKey = getTeamMemberSellerAddressKey();
        const scopedRaw = localStorage.getItem(memberKey);
        const legacyRaw = localStorage.getItem('sheenwear_seller_address');
        const addrData = scopedRaw
          ? JSON.parse(scopedRaw)
          : (legacyRaw ? JSON.parse(legacyRaw) : null);
        if (addrData && typeof addrData === 'object' && (addrData.addressLine1 || addrData.businessName)) {
          const patch = Object.fromEntries(
            Object.entries(addrData).filter(([, v]) => v !== undefined && v !== null)
          );
          setSellerAddress((prev) => ({ ...prev, ...patch }));
          // One-time migration from legacy key to per-member key.
          if (!scopedRaw) {
            localStorage.setItem(memberKey, JSON.stringify(addrData));
          }
        }
      } catch (e) {
        console.warn('Seller address load skip:', e?.message || e);
      }

      try {
        const chRes = await settingsService.getContactChannelsAdmin();
        const ch = chRes?.data || chRes;
        setWhatsappContact(mergeContactChannelsForForm(ch && typeof ch === 'object' ? ch : {}));
      } catch (e) {
        console.warn('Contact channels load skip:', e?.message || e);
        try {
          setWhatsappContact(mergeContactChannelsForForm({}));
        } catch (_) {
          /* ignore */
        }
      }

      try {
        const siteRes = await settingsService.getSite();
        const remote =
          siteRes && siteRes.data && typeof siteRes.data === 'object' ? siteRes.data : {};
        let local = {};
        try {
          const s = localStorage.getItem(SITE_SETTINGS_STORAGE_KEY);
          if (s) local = JSON.parse(s) || {};
        } catch (e) {
          /* ignore */
        }
        setSiteSiteSettings((prev) => ({ ...prev, ...local, ...remote }));
        const sitePhone = String(remote.sitePhone || local.sitePhone || '').trim();
        if (sitePhone) {
          setProfileData((prev) => ({
            ...prev,
            phone: prev.phone?.trim() ? prev.phone : sitePhone,
          }));
        }
        const siteDigits = normalizeWhatsappDigits(sitePhone);
        if (siteDigits) {
          setWhatsappContact((prev) => {
            const has =
              normalizeWhatsappDigits(prev.whatsappChatNumber) ||
              normalizeWhatsappDigits(prev.whatsappOrderNumber);
            if (has) return prev;
            return {
              ...prev,
              whatsappChatNumber: siteDigits,
              whatsappOrderNumber: siteDigits,
            };
          });
        }
      } catch (e) {
        console.warn('Site settings load skip:', e?.message || e);
      }

      try {
        const notificationKey = getTeamMemberSettingsKey('notification_settings');
        const savedNotificationSettings = JSON.parse(
          localStorage.getItem(notificationKey) || '{}'
        );
        if (savedNotificationSettings && typeof savedNotificationSettings === 'object') {
          setNotificationSettings((prev) => ({ ...prev, ...savedNotificationSettings }));
        }
      } catch (e) {
        console.warn('Team notification settings load skip:', e?.message || e);
      }

      try {
        const securityKey = getTeamMemberSettingsKey('security_settings');
        const savedSecuritySettings = JSON.parse(localStorage.getItem(securityKey) || '{}');
        if (savedSecuritySettings && typeof savedSecuritySettings === 'object') {
          setSecuritySettings((prev) => ({ ...prev, ...savedSecuritySettings }));
        }
      } catch (e) {
        console.warn('Team security settings load skip:', e?.message || e);
      }
    } catch (error) {
      console.warn('Profile load error, using localStorage:', error?.message || error);
      setProfileData({
        name: userDataFromStorage.name || 'Admin User',
        email: userDataFromStorage.email || 'admin@superwear.com',
        phone: extractPhoneFromUserLike(userDataFromStorage) || phoneFromSession() || '',
        avatar: userDataFromStorage.avatar || '',
        bio: userDataFromStorage.bio || '',
        department: userDataFromStorage.department || 'Administration',
        role: userDataFromStorage.role || 'admin',
        joinDate: userDataFromStorage.joinDate || new Date().toISOString(),
        lastLogin: userDataFromStorage.lastLogin || new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStoreLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canEditStoreLogo) return;
    const v = validateImageFile(file, 10);
    if (!v.isValid) {
      setError(v.errors.join(' '));
      return;
    }
    try {
      setStoreLogoUploading(true);
      setError(null);
      const { url } = await uploadAdminImage(file);
      setSiteSiteSettings((prev) => ({ ...prev, siteLogoUrl: url }));
      setSuccess('Logo uploaded. Click Save store logo to apply site-wide.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Store logo upload error:', err);
      setError(err?.message || 'Logo upload failed');
    } finally {
      setStoreLogoUploading(false);
    }
  };

  const clearStoreLogo = () => {
    if (!canEditStoreLogo) return;
    setSiteSiteSettings((prev) => ({ ...prev, siteLogoUrl: '' }));
  };

  const handleCreatorBannerFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !isTeamMember) return;
    const v = validateImageFile(file, 10);
    if (!v.isValid) {
      setError(v.errors.join(' '));
      return;
    }
    try {
      setCreatorBannerUploading(true);
      setError(null);
      const { url } = await uploadCreatorBannerImage(file);
      setCreatorStorefront((prev) => ({ ...prev, bannerUrl: url }));
      setSuccess('Banner uploaded. Click Save creator page to apply on the shop.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Creator banner upload error:', err);
      setError(err?.message || 'Banner upload failed');
    } finally {
      setCreatorBannerUploading(false);
    }
  };

  const saveCreatorStorefront = async () => {
    if (!isTeamMember) return;
    const userId = resolveAdminUserId();
    if (!userId) return;

    try {
      setSavingCreatorStorefront(true);
      setError(null);

      let slugToSave = creatorStorefront.creatorSlug;
      let slugCheck = validateCreatorSlug(slugToSave);
      if (!slugCheck.slug && profileData.name) {
        const fromName = validateCreatorSlug(suggestCreatorSlugFromName(profileData.name));
        if (fromName.valid && fromName.slug) {
          slugCheck = fromName;
          slugToSave = fromName.slug;
        }
      }
      if (!slugCheck.valid) {
        setError(slugCheck.error);
        return;
      }

      const payload = {
        creatorBannerUrl: creatorStorefront.bannerUrl || '',
        creatorHeadline: creatorStorefront.headline || '',
        creatorBio: creatorStorefront.bio || '',
        creatorSlug: slugCheck.slug,
      };
      if (payload.creatorSlug) rememberCreatorSlug(userId, payload.creatorSlug);

      const adminToken = localStorage.getItem('adminToken');
      try {
        const response = await fetch(`${getApiBaseUrlWithoutPath()}/api/admin/profile`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          console.warn('Creator storefront PUT returned', response.status);
        }
      } catch (apiErr) {
        console.warn('Creator storefront API save fallback to localStorage:', apiErr?.message || apiErr);
      }

      writeCreatorStorefrontToStorage(userId, {
        bannerUrl: payload.creatorBannerUrl,
        headline: payload.creatorHeadline,
        bio: payload.creatorBio,
        creatorSlug: payload.creatorSlug,
        name: profileData.name,
        role: profileData.role,
        avatar: profileData.avatar,
        updatedAt: new Date().toISOString(),
      });

      const existingUser = getAdminUser() || {};
      const mergedUser = {
        ...existingUser,
        creatorBannerUrl: payload.creatorBannerUrl,
        creatorHeadline: payload.creatorHeadline,
        creatorBio: payload.creatorBio,
        creatorSlug: payload.creatorSlug,
      };
      setAdminUser(mergedUser);
      window.dispatchEvent(new CustomEvent('adminProfileUpdated', { detail: mergedUser }));

      setSuccess('Creator page saved. Customers will see your banner on Products by Creator.');
      setTimeout(() => setSuccess(null), 3500);
    } catch (err) {
      console.error('Save creator storefront error:', err);
      setError(err?.message || 'Failed to save creator page');
    } finally {
      setSavingCreatorStorefront(false);
    }
  };

  const uploadPromoPopupImage = async (file, target) => {
    const v = validateImageFile(file, 5);
    if (!v.isValid) {
      setError(v.errors.join(' '));
      return;
    }
    try {
      setPromoPopupUploading(true);
      setPromoUploadTarget(target);
      setError(null);
      const localPreview = await createImagePreview(file);
      if (target === 'shop') setShopPromoImagePreview(localPreview);
      else setSitePromoImagePreview(localPreview);

      const { url } =
        target === 'shop'
          ? await uploadShopPromoPopupImage(file)
          : await uploadAdminImage(file, { purpose: 'sitePromoPopup' });

      const patch = {
        promoPopupImageUrl: url,
        promoPopupEnabled: true,
      };
      if (target === 'shop') {
        setShopkeeperPromo((prev) => ({ ...prev, ...patch }));
        setShopPromoImagePreview('');
      } else {
        setSiteSiteSettings((prev) => ({ ...prev, ...patch }));
        setSitePromoImagePreview('');
      }
      setSuccess('Promo image uploaded. Click Save on the promo card to apply.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Promo popup image upload error:', err);
      setError(err?.message || 'Upload failed');
      if (target === 'shop') setShopPromoImagePreview('');
      else setSitePromoImagePreview('');
    } finally {
      setPromoPopupUploading(false);
    }
  };

  const saveSitePromoSettings = async () => {
    if (!isMainAdmin) return;
    try {
      setSavingSitePromo(true);
      setError(null);
      const res = await settingsService.saveSite(siteSiteSettings);
      if (res && res.success === false) {
        throw new Error(res.error || res.message || 'Save failed');
      }
      notifySiteBrandingUpdated();
      setSuccess('Site promotional popup saved.');
      setTimeout(() => setSuccess(null), 3500);
    } catch (err) {
      console.error('Save site promo error:', err);
      setError(err?.message || 'Failed to save site promotional popup');
    } finally {
      setSavingSitePromo(false);
    }
  };

  const saveShopkeeperPromoSettings = async () => {
    if (!isTeamMember) return;
    const userId = resolveAdminUserId();
    if (!userId) return;

    const normalized = normalizePromoConfig(shopkeeperPromo);

    try {
      setSavingShopPromo(true);
      setError(null);

      const payload = {
        promoPopupEnabled: normalized.promoPopupEnabled,
        promoPopupImageUrl: normalized.promoPopupImageUrl,
        promoPopupDelaySeconds: normalized.promoPopupDelaySeconds,
        promoPopupAutoCloseSeconds: normalized.promoPopupAutoCloseSeconds,
      };

      const adminToken = localStorage.getItem('adminToken');
      let apiSaved = false;
      try {
        const response = await fetch(`${getApiBaseUrlWithoutPath()}/api/admin/profile`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const body = await response.json().catch(() => ({}));
        if (response.ok) {
          apiSaved = true;
          const profileLike =
            (body.profile && typeof body.profile === 'object' && body.profile) ||
            (body.data?.profile && typeof body.data.profile === 'object' && body.data.profile) ||
            (body.user && typeof body.user === 'object' && body.user) ||
            (body.data?.user && typeof body.data.user === 'object' && body.data.user) ||
            body.data ||
            body;
          const fromApi = mergeCreatorPromoSources(normalized, profileLike);
          if (fromApi) {
            Object.assign(normalized, fromApi);
          }
        } else {
          console.warn('Shopkeeper promo PUT returned', response.status, body?.message || body?.error);
        }
      } catch (apiErr) {
        console.warn('Shopkeeper promo API save fallback to localStorage:', apiErr?.message || apiErr);
      }

      writeCreatorPromoToStorage(userId, normalized);
      try {
        localStorage.setItem(
          getTeamMemberSettingsKey('promo_popup'),
          JSON.stringify(normalized)
        );
      } catch {
        /* ignore */
      }

      setShopkeeperPromo(normalized);
      setSuccess(
        apiSaved
          ? 'Your shop promotional popup was saved.'
          : 'Saved on this device. Backend did not confirm — visitors on other devices need API support (see docs/BACKEND_PROMPT_SHOPKEEPER_PROMO_UPLOAD.md).'
      );
      setTimeout(() => setSuccess(null), apiSaved ? 3500 : 6000);
    } catch (err) {
      console.error('Save shopkeeper promo error:', err);
      setError(err?.message || 'Failed to save shop promotional popup');
    } finally {
      setSavingShopPromo(false);
    }
  };

  const saveStoreBranding = async () => {
    if (!canEditStoreLogo) return;
    try {
      setSavingStoreBranding(true);
      setError(null);
      const res = await settingsService.saveSite(siteSiteSettings);
      if (res && res.success === false) {
        throw new Error(res.error || res.message || 'Save failed');
      }
      notifySiteBrandingUpdated();
      window.dispatchEvent(new CustomEvent('maintenanceModeChanged'));
      setSuccess('Store logo saved. It will show on the website, favicon, and invoices.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Save store branding error:', err);
      setError(err?.message || 'Failed to save store logo');
    } finally {
      setSavingStoreBranding(false);
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      const phoneTrimmed = String(profileData.phone || '').trim();
      const profilePayload = {
        name: profileData.name,
        email: profileData.email,
        phone: phoneTrimmed,
        bio: profileData.bio,
        department: profileData.department,
        avatar: profileData.avatar,
      };

      const adminToken = localStorage.getItem('adminToken');

      try {
        const response = await fetch(`${getApiBaseUrlWithoutPath()}/api/admin/profile`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(profilePayload),
        });
        if (!response.ok) {
          console.warn('Profile PUT returned', response.status);
        }
      } catch (apiErr) {
        console.warn('Profile API not available, using localStorage:', apiErr?.message || apiErr);
      }

      const userData = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const updatedUser = {
        ...userData,
        ...profilePayload,
        role: profileData.role || userData.role,
        joinDate: profileData.joinDate || userData.joinDate,
        lastLogin: profileData.lastLogin || userData.lastLogin,
      };
      setAdminUser(updatedUser);

      try {
        const nextSite = { ...siteSiteSettings, sitePhone: phoneTrimmed };
        await settingsService.saveSite(nextSite);
        setSiteSiteSettings((prev) => ({ ...prev, sitePhone: phoneTrimmed }));
        notifySiteBrandingUpdated();
      } catch (siteErr) {
        console.warn('Store phone sync:', siteErr?.message || siteErr);
      }

      const userId = resolveAdminUserId();
      if (userId && phoneTrimmed) {
        try {
          await adminUserService.updateUser(userId, { phone: phoneTrimmed });
        } catch (userErr) {
          console.warn('User phone update (optional):', userErr?.message || userErr);
        }
      }

      setProfileData((prev) => ({ ...prev, phone: phoneTrimmed }));

      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
      window.dispatchEvent(new CustomEvent('adminProfileUpdated', { detail: updatedUser }));
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswordFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const changePassword = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword?.trim()) {
      setError('Please enter your current password');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await adminUserService.changeOwnPassword({
        currentPassword: passwordData.currentPassword.trim(),
        newPassword: passwordData.newPassword.trim(),
      });

      if (res && res.success === false) {
        setError(res.error || res.message || 'Failed to change password');
        return;
      }

      setSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setShowPasswordFields({ current: false, new: false, confirm: false });
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      const { message } = parseThrownAdminError(error);
      setError(message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const saveSellerAddress = async () => {
    try {
      setLoading(true);
      setError(null);
      const memberKey = getTeamMemberSellerAddressKey();
      localStorage.setItem(memberKey, JSON.stringify(sellerAddress));
      setSuccess('Seller address saved for this team member. Invoice will use this address.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving seller address:', error);
      setError(error?.message || 'Failed to save seller address');
    } finally {
      setLoading(false);
    }
  };

  const saveContactChannels = async () => {
    try {
      setLoading(true);
      setError(null);
      const fresh = await settingsService.getContactChannelsAdmin();
      const base = normalizeContactChannelsData(fresh?.data || fresh || {});
      const payload = normalizeContactChannelsData({
        ...base,
        whatsappChatNumber: whatsappContact.whatsappChatNumber.trim(),
        whatsappOrderNumber: whatsappContact.whatsappOrderNumber.trim(),
      });
      const res = await settingsService.saveContactChannels(payload);
      if (res && res.success === false) throw new Error(res.error || res.message || 'Save failed');
      setSuccess('WhatsApp numbers saved. Checkout uses these for customer chat / order links.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (error) {
      console.error('Error saving contact channels:', error);
      setError(error?.message || 'Failed to save WhatsApp numbers');
    } finally {
      setLoading(false);
    }
  };

  const saveNotificationSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const notificationKey = getTeamMemberSettingsKey('notification_settings');
      localStorage.setItem(notificationKey, JSON.stringify(notificationSettings));
      // Keep existing listeners in sync for current admin session.
      try {
        localStorage.setItem(
          ADMIN_NOTIFICATION_SETTINGS_KEY,
          JSON.stringify(notificationSettings)
        );
      } catch (e) {
        /* ignore */
      }
      window.dispatchEvent(new CustomEvent(ADMIN_NOTIFICATION_FEED_PREFS_CHANGED));
      setSuccess('Notification settings saved for this team member.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving notification settings:', error);
      setError(error?.message || 'Failed to save notification settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSecuritySettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const securityKey = getTeamMemberSettingsKey('security_settings');
      localStorage.setItem(securityKey, JSON.stringify(securitySettings));
      setSuccess('Security settings saved for this team member.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving security settings:', error);
      setError(error?.message || 'Failed to save security settings');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !profileData.name) {
    return (
      <AdminLoading
        title="Loading Profile"
        subtitle="Fetching your account data..."
        icon="👤"
      />
    );
  }

  return (
    <div className="myaccount-container">
      <div className="myaccount-header">
        <div className="header-content">
          <h1>My Account</h1>
          <p>Manage your profile and account settings</p>
        </div>
        <div className="header-actions">
          <button onClick={loadProfileData} className="refresh-btn" disabled={loading}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="success-message">
          <i className="fas fa-check-circle"></i>
          {success}
          <button onClick={() => setSuccess(null)} className="close-message">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button onClick={() => setError(null)} className="close-message">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="myaccount-grid">

        {/* Account Statistics */}
        <div className="stats-card my-account-stats-card">
          <div className="card-header my-account-stats-card__header">
            <h3><i className="fas fa-chart-bar" aria-hidden /> Account Statistics</h3>
          </div>
          <div className="card-content my-account-stats-card__content">
            <div className="stats-grid my-account-stats-grid">
              <div className="stat-item my-account-stat-tile my-account-stat-tile--joined">
                <div className="stat-icon my-account-stat-tile__icon" aria-hidden>
                  <i className="fas fa-calendar-alt" />
                </div>
                <div className="stat-info my-account-stat-tile__body">
                  <div className="stat-label">Joined</div>
                  <div className="stat-value">{formatDate(profileData.joinDate)}</div>
                </div>
              </div>

              <div className="stat-item my-account-stat-tile my-account-stat-tile--login">
                <div className="stat-icon my-account-stat-tile__icon" aria-hidden>
                  <i className="fas fa-clock" />
                </div>
                <div className="stat-info my-account-stat-tile__body">
                  <div className="stat-label">Last Login</div>
                  <div className="stat-value">{formatDate(profileData.lastLogin)}</div>
                </div>
              </div>

              <div className="stat-item my-account-stat-tile my-account-stat-tile--role">
                <div className="stat-icon my-account-stat-tile__icon" aria-hidden>
                  <i className="fas fa-shield-alt" />
                </div>
                <div className="stat-info my-account-stat-tile__body">
                  <div className="stat-label">Role</div>
                  <div className="stat-value my-account-stat-tile__value--capitalize">
                    {profileData.role}
                  </div>
                </div>
              </div>

              <div className="stat-item my-account-stat-tile my-account-stat-tile--department">
                <div className="stat-icon my-account-stat-tile__icon" aria-hidden>
                  <i className="fas fa-building" />
                </div>
                <div className="stat-info my-account-stat-tile__body">
                  <div className="stat-label">Department</div>
                  <div className="stat-value">{profileData.department}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Creator storefront — team members only (Products by Creator page header) */}
        {isTeamMember ? (
          <div className="settings-card creator-storefront-card">
            <div className="card-header">
              <h3><i className="fas fa-store"></i> Creator Page (Shop Header)</h3>
              <button
                type="button"
                onClick={saveCreatorStorefront}
                className="save-settings-btn"
                disabled={savingCreatorStorefront || creatorBannerUploading}
              >
                <i className={`fas ${savingCreatorStorefront ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>{' '}
                Save
              </button>
            </div>
            <div className="card-content">
              <p className="section-description">
                Shown on the public &quot;Products by {profileData.name || 'you'}&quot; page when customers
                click <strong>Created by</strong> on your products.
              </p>
              <div className="form-group">
                <label>Header banner image</label>
                {creatorStorefront.bannerUrl ? (
                  <div className="creator-banner-preview-wrap">
                    <img
                      src={creatorStorefront.bannerUrl}
                      alt="Creator page banner preview"
                      className="creator-banner-preview"
                    />
                    <button
                      type="button"
                      className="btn-secondary-small"
                      onClick={() => setCreatorStorefront((prev) => ({ ...prev, bannerUrl: '' }))}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <p className="field-hint">Recommended: wide image, at least 1200×400 px.</p>
                )}
                <label className="upload-file-label">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleCreatorBannerFile}
                    disabled={creatorBannerUploading}
                  />
                  <span className="upload-file-btn">
                    <i className={`fas ${creatorBannerUploading ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>{' '}
                    {creatorBannerUploading ? 'Uploading…' : 'Upload banner'}
                  </span>
                </label>
              </div>
              <div className="form-group">
                <label htmlFor="creatorSlug">Shop URL</label>
                <div className="creator-slug-field">
                  <span className="creator-slug-prefix" aria-hidden="true">
                    /
                  </span>
                  <input
                    id="creatorSlug"
                    type="text"
                    className="form-input creator-slug-input"
                    value={creatorStorefront.creatorSlug ?? ''}
                    onChange={(e) =>
                      setCreatorStorefront((prev) => ({
                        ...prev,
                        creatorSlug: normalizeCreatorSlugInput(e.target.value),
                      }))
                    }
                    placeholder="your-shop-name"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
                <p className="field-hint">
                  Lowercase letters, numbers, and hyphens (3–40 characters). Your shop link becomes
                  /your-handle (e.g. /umardaraz).
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="creatorHeadline">Headline (optional)</label>
                <input
                  id="creatorHeadline"
                  type="text"
                  className="form-input"
                  value={creatorStorefront.headline ?? ''}
                  onChange={(e) =>
                    setCreatorStorefront((prev) => ({ ...prev, headline: e.target.value }))
                  }
                  placeholder="e.g. Handpicked styles by HHC"
                />
              </div>
              <div className="form-group">
                <label htmlFor="creatorBio">Short bio (optional)</label>
                <textarea
                  id="creatorBio"
                  className="form-textarea"
                  rows={3}
                  value={creatorStorefront.bio ?? ''}
                  onChange={(e) =>
                    setCreatorStorefront((prev) => ({ ...prev, bio: e.target.value }))
                  }
                  placeholder="Tell customers about your collection…"
                />
              </div>
              {resolveAdminUserId()
                ? (() => {
                    const uid = resolveAdminUserId();
                    const previewPath =
                      buildCreatorShopPath({
                        _id: uid,
                        creatorSlug: creatorStorefront.creatorSlug,
                      }) || `/${encodeURIComponent(uid)}`;
                    const origin =
                      (process.env.REACT_APP_FRONTEND_URL || '').replace(/\/+$/, '') ||
                      (typeof window !== 'undefined' ? window.location.origin : '') ||
                      'https://www.superwear.net';
                    const previewUrl = `${origin}${previewPath.startsWith('/') ? previewPath : `/${previewPath}`}`;
                    return (
                      <p className="field-hint">
                        Preview link:{' '}
                        <a href={previewPath} target="_blank" rel="noopener noreferrer">
                          {previewPath}
                        </a>
                        <span className="field-hint-sub"> ({previewUrl})</span>
                      </p>
                    );
                  })()
                : null}
            </div>
          </div>
        ) : null}

        {isTeamMember ? (
          <PromotionalPopupSettings
            variant="shopkeeper"
            settings={shopkeeperPromo}
            onChange={setShopkeeperPromo}
            onSave={saveShopkeeperPromoSettings}
            saving={savingShopPromo}
            uploading={promoPopupUploading && promoUploadTarget === 'shop'}
            onImageFile={(file) => uploadPromoPopupImage(file, 'shop')}
            onClearImage={() => {
              setShopPromoImagePreview('');
              setShopkeeperPromo((prev) => ({ ...prev, promoPopupImageUrl: '' }));
            }}
            imagePreviewUrl={shopPromoImagePreview}
          />
        ) : null}

        {isMainAdmin ? (
          <PromotionalPopupSettings
            variant="site"
            settings={siteSiteSettings}
            onChange={setSiteSiteSettings}
            onSave={saveSitePromoSettings}
            saving={savingSitePromo}
            uploading={promoPopupUploading && promoUploadTarget === 'site'}
            onImageFile={(file) => uploadPromoPopupImage(file, 'site')}
            onClearImage={() => {
              setSitePromoImagePreview('');
              setSiteSiteSettings((prev) => ({ ...prev, promoPopupImageUrl: '' }));
            }}
            imagePreviewUrl={sitePromoImagePreview}
            showPageTargets
          />
        ) : null}

        {/* Change Password */}
        <div className="password-card">
          <div className="card-header">
            <h3><i className="fas fa-lock"></i> Change Password</h3>
          </div>
          <div className="card-content">
            <form onSubmit={changePassword} className="password-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <div className="my-account-password-field-wrap">
                  <input
                    type={showPasswordFields.current ? 'text' : 'password'}
                    id="currentPassword"
                    value={passwordData.currentPassword ?? ''}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className="form-input my-account-password-input"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="my-account-password-toggle"
                    onClick={() => togglePasswordVisibility('current')}
                    aria-label={showPasswordFields.current ? 'Hide current password' : 'Show current password'}
                  >
                    <i className={`fas ${showPasswordFields.current ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="my-account-password-field-wrap">
                  <input
                    type={showPasswordFields.new ? 'text' : 'password'}
                    id="newPassword"
                    value={passwordData.newPassword ?? ''}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="form-input my-account-password-input"
                    autoComplete="new-password"
                    required
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="my-account-password-toggle"
                    onClick={() => togglePasswordVisibility('new')}
                    aria-label={showPasswordFields.new ? 'Hide new password' : 'Show new password'}
                  >
                    <i className={`fas ${showPasswordFields.new ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <div className="my-account-password-field-wrap">
                  <input
                    type={showPasswordFields.confirm ? 'text' : 'password'}
                    id="confirmPassword"
                    value={passwordData.confirmPassword ?? ''}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="form-input my-account-password-input"
                    autoComplete="new-password"
                    required
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="my-account-password-toggle"
                    onClick={() => togglePasswordVisibility('confirm')}
                    aria-label={showPasswordFields.confirm ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    <i className={`fas ${showPasswordFields.confirm ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                  </button>
                </div>
              </div>
              
              <button type="submit" className="btn-change-password" disabled={loading}>
                <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-key'}`}></i>
                Change Password
              </button>
            </form>
          </div>
        </div>

        {/* Seller / Shipping Address - Used in Invoices */}
        <div className="settings-card">
          <div className="card-header">
            <h3><i className="fas fa-map-marker-alt"></i> Seller / Shipping Address</h3>
            <button onClick={saveSellerAddress} className="save-settings-btn" disabled={loading}>
              <i className="fas fa-save"></i> Save
            </button>
          </div>
          <div className="card-content">
            <p className="section-description">This address appears on invoices as the seller/company address.</p>
            <div className="form-group">
              <label>Business Name</label>
              <input
                type="text"
                value={sellerAddress.businessName ?? ''}
                onChange={(e) => setSellerAddress({...sellerAddress, businessName: e.target.value})}
                className="form-input"
                placeholder="Super Wear"
              />
            </div>
            <div className="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                value={sellerAddress.addressLine1 ?? ''}
                onChange={(e) => setSellerAddress({...sellerAddress, addressLine1: e.target.value})}
                className="form-input"
                placeholder="123 Fashion Street"
              />
            </div>
            <div className="form-group">
              <label>Address Line 2 (optional)</label>
              <input
                type="text"
                value={sellerAddress.addressLine2 ?? ''}
                onChange={(e) => setSellerAddress({...sellerAddress, addressLine2: e.target.value})}
                className="form-input"
                placeholder="Suite, Floor, etc."
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={sellerAddress.city ?? ''}
                  onChange={(e) => setSellerAddress({...sellerAddress, city: e.target.value})}
                  className="form-input"
                  placeholder="Karachi"
                />
              </div>
              <div className="form-group">
                <label>State / Province</label>
                <input
                  type="text"
                  value={sellerAddress.state ?? ''}
                  onChange={(e) => setSellerAddress({...sellerAddress, state: e.target.value})}
                  className="form-input"
                  placeholder="Sindh"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  value={sellerAddress.postalCode ?? ''}
                  onChange={(e) => setSellerAddress({...sellerAddress, postalCode: e.target.value})}
                  className="form-input"
                  placeholder="75000"
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  value={sellerAddress.country ?? ''}
                  onChange={(e) => setSellerAddress({...sellerAddress, country: e.target.value})}
                  className="form-input"
                  placeholder="Pakistan"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={sellerAddress.phone ?? ''}
                  onChange={(e) => setSellerAddress({...sellerAddress, phone: e.target.value})}
                  className="form-input"
                  placeholder="+92 300 1234567"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={sellerAddress.email ?? ''}
                  onChange={(e) => setSellerAddress({...sellerAddress, email: e.target.value})}
                  className="form-input"
                  placeholder="info@superwear.com"
                />
              </div>
            </div>
          </div>
        </div>


        {/* Notification Settings */}
        <div className="settings-card">
          <div className="card-header">
            <h3><i className="fas fa-bell"></i> Notification Settings</h3>
            <button onClick={saveNotificationSettings} className="save-settings-btn" disabled={loading}>
              <i className="fas fa-save"></i> Save
            </button>
          </div>
          <div className="card-content">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!notificationSettings.emailNotifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, emailNotifications: e.target.checked })}
                />
                <span className="checkmark"></span>
                Email Notifications
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!notificationSettings.smsNotifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, smsNotifications: e.target.checked })}
                />
                <span className="checkmark"></span>
                SMS Notifications
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!notificationSettings.pushNotifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, pushNotifications: e.target.checked })}
                />
                <span className="checkmark"></span>
                Push Notifications
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!notificationSettings.orderNotifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, orderNotifications: e.target.checked })}
                />
                <span className="checkmark"></span>
                Order Notifications
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!notificationSettings.stockNotifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, stockNotifications: e.target.checked })}
                />
                <span className="checkmark"></span>
                Stock Notifications
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!notificationSettings.userNotifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, userNotifications: e.target.checked })}
                />
                <span className="checkmark"></span>
                User Notifications
              </label>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="settings-card">
          <div className="card-header">
            <h3><i className="fas fa-shield-alt"></i> Security Settings</h3>
            <button onClick={saveSecuritySettings} className="save-settings-btn" disabled={loading}>
              <i className="fas fa-save"></i> Save
            </button>
          </div>
          <div className="card-content">
            <div className="form-group">
              <label>Session Timeout (minutes)</label>
              <input
                type="number"
                value={securitySettings.sessionTimeout ?? 30}
                onChange={(e) =>
                  setSecuritySettings({ ...securitySettings, sessionTimeout: parseInt(e.target.value, 10) || 0 })
                }
                className="form-input"
                min="5"
                max="1440"
              />
            </div>
            <div className="form-group">
              <label>Max Login Attempts</label>
              <input
                type="number"
                value={securitySettings.maxLoginAttempts ?? 5}
                onChange={(e) =>
                  setSecuritySettings({ ...securitySettings, maxLoginAttempts: parseInt(e.target.value, 10) || 0 })
                }
                className="form-input"
                min="1"
                max="20"
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!securitySettings.requireStrongPassword}
                  onChange={(e) =>
                    setSecuritySettings({ ...securitySettings, requireStrongPassword: e.target.checked })
                  }
                />
                <span className="checkmark"></span>
                Require Strong Password
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!securitySettings.twoFactorAuth}
                  onChange={(e) =>
                    setSecuritySettings({ ...securitySettings, twoFactorAuth: e.target.checked })
                  }
                />
                <span className="checkmark"></span>
                Enable Two-Factor Authentication
              </label>
            </div>
            <div className="form-group">
              <label>IP Whitelist (comma separated)</label>
              <textarea
                value={securitySettings.ipWhitelist ?? ''}
                onChange={(e) => setSecuritySettings({ ...securitySettings, ipWhitelist: e.target.value })}
                className="form-textarea"
                rows="3"
                placeholder="192.168.1.1, 10.0.0.1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;